import { istLeererBlocktext } from "../notizbloecke/blockModell";
import { notizKlartext } from "../vorgang/v4/archivZeilen";
import { nurFremde, type KundenNotizDto } from "../kundentagebuch/kundenNotizen";
import { vorgangSprungLink } from "../vorgang/v4/sprungLink";

/**
 * Die Notizen aus Vorgängen und To-dos als Verlaufseinträge des Kunden
 * (PO 14.09.2026: „kundentagebuch da nur neue notizen, notizen zu vorgang und
 * todos im archiv anzeigen"). Reine Ableitung, ohne React und ohne Fetch —
 * dasselbe Muster wie `kommunikationEintraege.ts` nebenan.
 *
 * ══ NUR LESBAR ═════════════════════════════════════════════════════════════
 * Eine Vorgangs-Notiz gehört ihrem Vorgang. Ein zweiter Bearbeitungsort für
 * denselben Text wäre eine Einladung zum Auseinanderlaufen — deshalb trägt der
 * Eintrag den Vorgang als SPRUNGZIEL und keine Aktion: geändert wird dort, wo
 * die Notiz zu Hause ist.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export type NotizEintrag = {
    /** Stabiler Schlüssel — mit der Quelle, denn `vorgang:5` und `todo:5`
     *  stehen nebeneinander (zwei Tabellen, zwei Id-Folgen). */
    id: string;
    /** Zeitpunkt der Notiz — Sortierung und Zeitbereichs-Filter hängen daran. */
    ts: Date;
    /** Anriss des Notiztextes, ohne HTML — der Titel der Zeile. */
    titel: string;
    /** Der ganze Notiztext als Klartext (Aufklappbereich). */
    text: string;
    /** „aus Vorgang „X"" bzw. „aus To-do „Y" (X)", ggf. mit „· über …". */
    herkunft: string;
    /** Benutzerschlüssel des Autors; den Namen löst der Aufrufer auf. */
    autor: string;
    /** Vorgang, in dem die Notiz zu Hause ist; null = nichts zu öffnen. */
    vorgangId: number | null;
    /**
     * Wohin der Klick führt: der Vorgang MIT aufgeklapptem To-do und der
     * Sprungmarke auf die Notiz (User 01.11.2026); null = nichts zu öffnen.
     */
    ziel: string | null;
};

const zeit = (iso?: string | null): Date => {
    if (!iso) return new Date(0);
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? new Date(0) : d;
};

/** Erste `max` Zeichen, an einer Wortgrenze abgeschnitten; leer bleibt leer. */
const kuerze = (s: string, max: number): string => {
    if (s.length <= max) return s;
    const schnitt = s.slice(0, max);
    const luecke = schnitt.lastIndexOf(" ");
    return `${(luecke > max * 0.6 ? schnitt.slice(0, luecke) : schnitt).trimEnd()} …`;
};

/**
 * Die Herkunft im Klartext. Sie kommt FERTIG aus dem Backend
 * (`herkunftLabel`) — dieselbe Beschriftung, die am Block stünde; zwei
 * Fassungen desselben Satzes liefen sonst auseinander. Der Rückfall greift nur,
 * wenn eine DTO ohne Beschriftung ankommt, und nennt bewusst keinen Titel,
 * statt die Backend-Regel hier ein zweites Mal nachzubauen.
 */
const herkunftText = (n: KundenNotizDto): string => {
    const label = n.herkunftLabel?.trim();
    if (label) return label;
    // `source` und nicht `herkunft.art`: dieselbe Angabe, aber es ist das Feld,
    // an dem die ganze Trennung hängt (`nurEigene`/`nurFremde`).
    return n.source === "todo" ? "aus einem To-do" : "aus einem Vorgang";
};

export function notizEintraege(notizen: KundenNotizDto[]): NotizEintrag[] {
    const out: NotizEintrag[] = [];
    for (const n of nurFremde(notizen)) {
        // INHALTSLEERE Notizen sind keine Einträge (PO 13.09.2026:
        // „leergeräumt heißt gelöscht") — dieselbe Regel, die auch die
        // Notizfläche anwendet, aus derselben Quelle.
        if (istLeererBlocktext(n.text)) continue;
        const klartext = notizKlartext(n.text, n.format);
        if (!klartext) continue;
        out.push({
            id: `notiz-${n.source}-${n.id}`,
            ts: zeit(n.timestamp),
            titel: kuerze(klartext, 90),
            text: klartext,
            herkunft: herkunftText(n),
            autor: n.author ?? "",
            vorgangId: n.herkunft.vorgangId,
            ziel:
                n.herkunft.vorgangId != null
                    ? vorgangSprungLink(n.herkunft.vorgangId, {
                          todoId: n.herkunft.todoId,
                          notiz: n.source === "kontakt" ? null : { quelle: n.source, id: n.id },
                      })
                    : null,
        });
    }
    return out;
}
